import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject,firstValueFrom, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AppLanguage } from './language.service';
import { VenuePhotoService } from './venue-photo.service';

export interface MyProfileData {
  id: number;
  publicId: string;
  email: string;
  displayName: string;
  socialName: string;
  bio: string;
  phone: string;
  language: AppLanguage;
  isMusician?: boolean;
  isProfileApproved?: boolean;
  primaryRole: number;
  profilePhoto: string | null;
  googlePlaceId?: string;
  latitude: number | null;
  longitude: number | null;
  isProfileComplete: boolean;
  missingProfileFields: string[];
  shouldShowOnboarding: boolean;
  onboardingPromptedAt: string | null;
  onboardingCompletedAt: string | null;
  // Artist-specific
  memberCount?: number | null;
  managementContact?: string;
  website?: string;
  artistStyleIds?: number[];
  artistStyleNames?: string[];
  // Venue-specific
  capacity?: number | null;
  addressText?: string;
  openingHoursText?: string;
}

export interface UpdateProfilePayload {
  displayName?: string;
  socialName?: string;
  bio?: string;
  phone?: string;
  language?: AppLanguage;
  latitude?: number | null;
  longitude?: number | null;
  musicianRadiusKm?: number;
  // Artist
  memberCount?: number | null;
  managementContact?: string;
  website?: string;
  artistStyleIds?: number[];
  // Venue
  capacity?: number | null;
  addressText?: string;
  openingHoursText?: string;
}

export interface ChangeAccountTypePayload {
  primaryRole: number;
  displayName?: string;
  bio?: string;
  // Artist (role 3)
  memberCount?: number | null;
  // Venue (role 2)
  socialName?: string;
  capacity?: number | null;
  addressText?: string;
}

export interface RiderSection {
  id: number;
  title: string;
  body: string;
  sortOrder: number;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;
  private readonly venuePhotos = inject(VenuePhotoService);
  private readonly myProfileSubject = new BehaviorSubject<MyProfileData | null>(null);
  readonly myProfile$ = this.myProfileSubject.asObservable(); 
  readonly profileIncomplete$ = this.myProfile$.pipe(
    map(profile => !!profile && this.requiresProfileApproval(profile))
  );

  async fetchMyProfile(): Promise<MyProfileData> {
    const raw = await firstValueFrom(
      this.http.get<Record<string, unknown>>(`${this.base}/api/profile/me`),
    );

    const profile = await this.mapProfile(raw);
    this.myProfileSubject.next(profile);

    return profile;
  }

  async updateMyProfile(payload: UpdateProfilePayload): Promise<MyProfileData> {
    const form = new FormData();
    if (payload.displayName !== undefined) form.append('display_name', payload.displayName);
    if (payload.socialName !== undefined) form.append('social_name', payload.socialName);
    if (payload.bio !== undefined) form.append('bio', payload.bio);
    if (payload.phone !== undefined) form.append('phone', payload.phone);
    if (payload.language !== undefined) form.append('language', payload.language);
    if (payload.latitude !== undefined) form.append('latitude', payload.latitude !== null ? String(payload.latitude) : '');
    if (payload.longitude !== undefined) form.append('longitude', payload.longitude !== null ? String(payload.longitude) : '');
    if (payload.musicianRadiusKm !== undefined) form.append('musician_radius_km', String(payload.musicianRadiusKm));
    // Artist
    if (payload.memberCount !== undefined) form.append('member_count', payload.memberCount !== null ? String(payload.memberCount) : '');
    if (payload.managementContact !== undefined) form.append('management_contact', payload.managementContact);
    if (payload.website !== undefined) form.append('website', payload.website);
    if (payload.artistStyleIds !== undefined) form.append('artist_style_ids', JSON.stringify(payload.artistStyleIds));
    // Venue
    if (payload.capacity !== undefined) form.append('capacity', payload.capacity !== null ? String(payload.capacity) : '');
    if (payload.addressText !== undefined) form.append('address_text', payload.addressText);
    if (payload.openingHoursText !== undefined) form.append('opening_hours_text', payload.openingHoursText);

    const raw = await firstValueFrom(
      this.http.patch<Record<string, unknown>>(`${this.base}/api/profile/me`, form),
    );

    const profile = await this.mapProfile(raw);
    this.myProfileSubject.next(profile);

    return profile;
    }

  async uploadProfilePhoto(file: File): Promise<string | null> {
    const form = new FormData();
    form.append('photo_file', file);

    const response = await firstValueFrom(
      this.http.post<{ profile_photo: string }>(`${this.base}/api/profile/me/photo`, form),
    );

    await this.fetchMyProfile();

    return response.profile_photo ?? null;
  }

  async deleteProfilePhoto(): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/api/profile/me/photo`));
    await this.fetchMyProfile();
  }

  async createExternalLink(url: string): Promise<void> {
    const form = new FormData();
    form.append('platform', 'social');
    form.append('label', 'Red social');
    form.append('url', url);

    await firstValueFrom(
      this.http.post(`${this.base}/api/external-links`, form),
    );

    await this.fetchMyProfile();
  }

  async changeAccountType(payload: ChangeAccountTypePayload): Promise<void> {
    const body: Record<string, unknown> = { primary_role: payload.primaryRole };

    if (payload.displayName) body['display_name'] = payload.displayName;
    if (payload.bio) body['bio'] = payload.bio;
    if (payload.memberCount != null) body['member_count'] = payload.memberCount;
    if (payload.socialName) body['social_name'] = payload.socialName;
    if (payload.capacity != null) body['capacity'] = payload.capacity;
    if (payload.addressText) body['address_text'] = payload.addressText;

    await firstValueFrom(
      this.http.post(`${this.base}/api/profile/me/account-type`, body),
    );

    await this.fetchMyProfile();
  }

  async deleteMyAccount(confirmation: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.base}/api/profile/me/account`, {
        body: { confirmation },
      }),
    );
  }

  async fetchRiderSections(): Promise<RiderSection[]> {
    const raw = await firstValueFrom(
      this.http.get<Record<string, unknown>[]>(`${this.base}/api/rider-sections`),
    );
    return raw.map((s) => this.mapSection(s));
  }

  async createRiderSection(title: string, body: string): Promise<RiderSection> {
    const form = new FormData();
    form.append('title', title);
    form.append('body', body);
    const raw = await firstValueFrom(
      this.http.post<Record<string, unknown>>(`${this.base}/api/rider-sections`, form),
    );
    return this.mapSection(raw);
  }

  async updateRiderSection(id: number, title: string, body: string): Promise<RiderSection> {
    const form = new FormData();
    form.append('title', title);
    form.append('body', body);
    const raw = await firstValueFrom(
      this.http.patch<Record<string, unknown>>(`${this.base}/api/rider-sections/${id}`, form),
    );
    return this.mapSection(raw);
  }

  async deleteRiderSection(id: number): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/api/rider-sections/${id}`));
  }

  private async mapProfile(raw: Record<string, unknown>): Promise<MyProfileData> {
    const googlePlaceId = String(raw['google_place_id'] ?? '');
    const photo = await this.venuePhotos.resolve(
      (raw['profile_photo'] as string | null) ?? null,
      googlePlaceId,
    );
    return {
      id: raw['id'] as number,
      publicId: String(raw['public_id'] ?? ''),
      email: String(raw['email'] ?? ''),
      displayName: String(raw['display_name'] ?? ''),
      socialName: String(raw['social_name'] ?? ''),
      bio: String(raw['bio'] ?? ''),
      phone: String(raw['phone'] ?? ''),
      language: (raw['language'] as AppLanguage | undefined) ?? 'es',
      isMusician: Boolean(raw['is_musician']),
      isProfileApproved: Boolean(raw['is_profile_approved']),
      primaryRole: Number(raw['primary_role']),
      profilePhoto: photo?.url ?? null,
      googlePlaceId,
      latitude: (raw['latitude'] as number | null) ?? null,
      longitude: (raw['longitude'] as number | null) ?? null,
      isProfileComplete: Boolean(raw['is_profile_complete']),
      missingProfileFields: (raw['missing_profile_fields'] as string[] | undefined) ?? [],
      shouldShowOnboarding: Boolean(raw['should_show_onboarding']),
      onboardingPromptedAt: (raw['onboarding_prompted_at'] as string | null) ?? null,
      onboardingCompletedAt: (raw['onboarding_completed_at'] as string | null) ?? null,
      memberCount: (raw['member_count'] as number | null | undefined) ?? null,
      managementContact: String(raw['management_contact'] ?? ''),
      website: String(raw['website'] ?? ''),
      artistStyleIds: (raw['artist_style_ids'] as number[] | undefined) ?? [],
      artistStyleNames: (raw['artist_style_names'] as string[] | undefined) ?? [],
      capacity: (raw['capacity'] as number | null | undefined) ?? null,
      addressText: String(raw['address_text'] ?? ''),
      openingHoursText: String(raw['opening_hours_text'] ?? ''),
    };
  }

  private requiresProfileApproval(profile: MyProfileData): boolean {
    return (profile.primaryRole === 2 || profile.primaryRole === 3) && !profile.isProfileApproved;
  }

  private mapSection(raw: Record<string, unknown>): RiderSection {
    return {
      id: raw['id'] as number,
      title: String(raw['title'] ?? ''),
      body: String(raw['body'] ?? ''),
      sortOrder: Number(raw['sort_order'] ?? 0),
    };
  }
}
